import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Images } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../../components/layout/content/ContentPanelHeader";

import EmptyState from "../../../../../components/feedback/EmptyState";
import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import Button from "../../../../../components/buttons/Button";
import AlbumThumbnailCard from "../../../../../components/cards/thumbnails/albums/AlbumThumbnailCard";

import { useGroupAlbums } from "../hooks/useGroupAlbums";

import "./GroupMemberAlbumsView.css";

const DEFAULT_LIMIT = 12;
const PREVIEW_LIMIT = 5;

const GRID_CONFIG = {
  columns: 4,
  tabletColumns: 3,
  mobileColumns: 2,
  gap: 16,
  showEmpty: false,
};

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.message || "Unable to load group albums.";

export default function GroupMemberAlbumsView({
  groupTag,
  limit = DEFAULT_LIMIT,
  preview = false,
  onContentLoaded,
  onPreviewResult,
}) {
  const navigate = useNavigate();

  const pageLimit = preview
    ? PREVIEW_LIMIT
    : Math.max(1, Number(limit) || DEFAULT_LIMIT);

  const { albums, loading, loadingMore, error, hasMore, loadMoreAlbums } =
    useGroupAlbums({
      groupTag,
      limit: pageLimit,
    });

  const albumList = Array.isArray(albums) ? albums : [];

  const visibleAlbums = preview ? albumList.slice(0, PREVIEW_LIMIT) : albumList;

  const errorMessage = error ? getErrorMessage(error) : "";
  const isEmpty = !loading && visibleAlbums.length === 0;

  useEffect(() => {
    if (!preview || loading) return;

    const notify = onContentLoaded || onPreviewResult;

    notify?.({
      hasContent: visibleAlbums.length > 0,
      error: Boolean(errorMessage),
    });
  }, [
    preview,
    loading,
    visibleAlbums.length,
    errorMessage,
    onContentLoaded,
    onPreviewResult,
  ]);

  const handleOpenAlbum = (album) => {
    const albumTag = typeof album === "string" ? album : album?.albumTag;

    if (!albumTag) return;

    navigate(`/albums/${encodeURIComponent(albumTag)}`);
  };

  const handleLoadMore = async () => {
    if (preview || loading || loadingMore || !hasMore) return;

    await loadMoreAlbums();
  };

  const renderAlbum = (album) => (
    <AlbumThumbnailCard
      key={album.albumId ?? album.albumTag}
      album={album}
      onClick={handleOpenAlbum}
    />
  );

  if (preview && isEmpty && !errorMessage) {
    return null;
  }

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title={`Group Albums (${visibleAlbums.length})`}
          subTitle="Albums shared with this group."
        />

        {errorMessage && visibleAlbums.length > 0 && (
          <div className="group-albums__alert" role="alert">
            {errorMessage}
          </div>
        )}

        {loading && !visibleAlbums.length ? (
          <ContentGrid
            {...GRID_CONFIG}
            items={[]}
            loading
            skeletonCount={preview ? PREVIEW_LIMIT : 8}
            renderItem={() => null}
          />
        ) : !visibleAlbums.length ? (
          !preview && (
            <EmptyState
              icon={<Images size={28} />}
              title={
                errorMessage ? "Unable to load albums" : "No albums available"
              }
              description={
                errorMessage ||
                "Albums shared with this group will appear here."
              }
            />
          )
        ) : (
          <ContentGrid
            {...GRID_CONFIG}
            items={visibleAlbums}
            renderItem={renderAlbum}
          />
        )}

        {!preview && hasMore && visibleAlbums.length > 0 && (
          <div className="group-albums__footer">
            <Button
              value={loadingMore ? "Loading..." : "Load More"}
              variant="primary-outlined"
              disabled={loading || loadingMore}
              onClick={handleLoadMore}
            />
          </div>
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
